import { useEffect, useState } from "react";

import { consoleErrorWithSentry } from "../../../utils";
import { useSlackApi } from "./useSlackApi";

export type SlackIntegrationStatus = {
  isConnected: boolean;
  isLoading: boolean;
  hasPrivateChannelsScope: boolean;
  error: Error | null;
};

export const useSlackIntegration = (enabled: boolean = true): SlackIntegrationStatus => {
  const [slackAppAuth, setSlackAppAuth] = useState<{ ok: boolean; private_channels_scopes: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { authTest } = useSlackApi();

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const fetchSlackAuth = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await authTest();
        setSlackAppAuth(response.data);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to check Slack integration");
        setError(error);
        consoleErrorWithSentry(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSlackAuth();
  }, [authTest, enabled]);

  return {
    isConnected: slackAppAuth?.ok ?? false,
    isLoading,
    hasPrivateChannelsScope: slackAppAuth?.private_channels_scopes ?? false,
    error,
  };
};
